import { Box, Button } from '@mui/material';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import UserBookingAvailabilityDTO from '@src/adapters/dto/user/UserBookingAvailabilityDTO';
import { JobBookingStatus } from '@src/constants/JobBookingStatus';
import { useMemo, useState } from 'react';
import JobSelection from './JobSelection';
import { AvailabilityState } from '@src/constants/AvailabilityState';
import CheckIcon from '@mui/icons-material/Check';
import { isBefore } from 'date-fns';

interface Props {
  availability?: UserBookingAvailabilityDTO;
  onAvailabilityClicked(availability: UserBookingAvailabilityDTO): void;
  onJobSelected(availabilityId: string, jobId: string): void;
  jobs: JobDTO[];
  roles: string[];
  date: Date;
}

function CrewButton({
  availability,
  onAvailabilityClicked,
  onJobSelected,
  jobs,
  roles,
  date,
}: Props) {
  const [isChecked, setIsChecked] = useState(false);

  const isEnabled =
    availability &&
    (availability.jobStatus === JobBookingStatus.PencilAccepted ||
      isBefore(date, new Date()));

  const buttonText = useMemo<string>(() => {
    switch (availability?.jobStatus) {
      case JobBookingStatus.Booked:
        return 'Booked';
      case JobBookingStatus.Confirmed:
        return 'Confirmed';
      case JobBookingStatus.PencilAccepted:
        return 'Pencil (A)';
      case JobBookingStatus.Pencilled:
        return 'Pencilled';
      default:
        return 'Unassigned';
    }
  }, [availability]);

  const getButtonColor = (status?: JobBookingStatus) => {
    if (status === JobBookingStatus.PencilAccepted) {
      return 'warning';
    } else {
      return 'success';
    }
  };

  const handleJobSelected = (jobId: string) => {
    if (availability) {
      onJobSelected(availability.id!, jobId);
    }
  };

  const handleAvailabilityClicked = (
    availability: UserBookingAvailabilityDTO
  ) => {
    if (isEnabled) {
      setIsChecked(!isChecked);
      onAvailabilityClicked(availability);
    }
  };

  return (
    <>
      {availability &&
        !availability.jobStatus &&
        availability.state === AvailabilityState.NotAvailable && (
          <Box>Not Available</Box>
        )}
      {availability &&
        !availability.jobStatus &&
        availability.state === AvailabilityState.Available &&
        !isEnabled && (
          <JobSelection
            jobs={jobs}
            onJobSelected={handleJobSelected}
            roles={roles}
            date={date}
          />
        )}
      {availability && availability.jobStatus && (
        <Button
          variant={
            [
              JobBookingStatus.Pencilled,
              JobBookingStatus.Confirmed,
              JobBookingStatus.PencilAccepted,
              JobBookingStatus.Booked,
            ].includes(availability.jobStatus)
              ? 'contained'
              : 'outlined'
          }
          color={getButtonColor(availability.jobStatus)}
          onClick={(e) => {
            if (isEnabled) {
              handleAvailabilityClicked(availability);
            } else {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          sx={(theme) => ({
            backgroundColor:
              JobBookingStatus.Pencilled === availability.jobStatus ||
              JobBookingStatus.Booked === availability.jobStatus
                ? theme.palette.success.light
                : undefined,
            '&:hover': isEnabled ? { cursor: 'not-allowed' } : {},
          })}>
          {buttonText} {isChecked && <CheckIcon />}
        </Button>
      )}
    </>
  );
}

export default CrewButton;
